import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { EmblaCarouselType, EmblaOptionsType, EmblaPluginType } from 'embla-carousel';
import { CarouselContextValues, CarouselProvider } from './Carousel.context';
import { ResponsiveStyleValue } from '../../theming';
import { CarouselOrientation, CarouselOverflow } from './Carousel.types';
import { useCulture } from '../../culture';

export interface CarouselProps {
    /**
     * Additonal options applied to the embla carousel
     */
    emblaOptions?: EmblaOptionsType;

    /**
     * An array of embla plugins.
     */
    emblaPlugins?: EmblaPluginType[];

    /**
     * Returns a reference to the embla API.
     */
    getEmblaApi?: (embla: EmblaCarouselType) => void;

    /**
     * The child Slide components and any navigation controls.
     */
    children?: React.ReactNode;

    /**
     * The number of spacing units that will be applied between carousel slides.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default 4
     */
    slideSpacing?: ResponsiveStyleValue<number>;

    /** Controls slide width based on viewport width @default `'100%'` */

    /**
     * The number of items that will be visible in the viewport.
     * If `auto`, items will retain their natural width.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default 'auto'
     */
    visibleSlides?: ResponsiveStyleValue<number | 'auto'>;

    /**
     * The orientation of the carousel.
     * @default horizontal
     */
    orientation?: CarouselOrientation;

    /**
     * Sets the overflow behaviour. If `visible`, items which overflow the carousel viewport will still be visible.
     * @default hidden
     */
    overflow?: CarouselOverflow;

    /**
     * The index of the slide which will be initially centered in the carousel viewport.
     * @default 0
     */
    initialSlide?: number;

    /**
     * Callback fired when the slide changes.
     */
    onSlideChange?: (index: number) => void;

    /**
     * If `true`, arrow keys can be used to navigate between slides.
     * @default true
     */
    withKeyboardEvents?: boolean;

    /**
     * A title for the carousel. Used for accessibility markup.
     */
    title: string;
}

/**
 * A responsive carousel component based on the Embla Carousel library.
 * Carousels are typically used to display rows of cards or slides.
 */
export function Carousel(props: Readonly<CarouselProps>): React.JSX.Element {
    const {
        children,
        emblaOptions,
        emblaPlugins,
        getEmblaApi,
        slideSpacing = 4,
        visibleSlides = 'auto',
        orientation = 'horizontal',
        overflow = 'hidden',
        initialSlide = 0,
        onSlideChange,
        withKeyboardEvents = true,
        title,
    } = props;

    const { dir } = useCulture();

    // the selected slide index
    const [selected, setSelected] = useState(0);

    const userInitiatedRef = React.useRef(false);

    const handleWatchDrag = useCallback((api: EmblaCarouselType) => {
        // disable drag if there's nothing to scroll
        return api.canScrollNext() || api.canScrollPrev();
    }, []);

    const options: EmblaOptionsType = {
        startIndex: initialSlide,
        watchDrag: handleWatchDrag,
        direction: dir,
        ...emblaOptions,
    };

    const [emblaRefElement, emblaApi] = useEmblaCarousel(options, emblaPlugins);

    const handleSelect = useCallback(() => {
        if (!emblaApi) {
            return;
        }
        const slideIndex = emblaApi.selectedScrollSnap();
        setSelected(slideIndex);
        if (slideIndex !== selected) {
            onSlideChange?.(slideIndex);
        }
    }, [emblaApi, setSelected, onSlideChange, selected]);

    const handleKeydown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (!withKeyboardEvents || !emblaApi) return;
            if (event.key === 'ArrowRight') {
                event.preventDefault();
                emblaApi.scrollNext();
            }
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                emblaApi.scrollPrev();
            }
        },
        [emblaApi, withKeyboardEvents]
    );

    const markUserInitiated = useCallback(() => {
        userInitiatedRef.current = true;
    }, []);

    useEffect(() => {
        if (!emblaApi) {
            return undefined;
        }

        getEmblaApi?.(emblaApi);
        handleSelect();

        // --- Mark manual drags / swipes as user-initiated ---
        const handleDragStart = () => {
            markUserInitiated();
        };

        emblaApi.on('select', handleSelect);
        emblaApi.on('reInit', handleSelect);
        emblaApi.on('pointerDown', handleDragStart);

        return () => {
            emblaApi.off('select', handleSelect);
            emblaApi.off('reInit', handleSelect);
            emblaApi.off('pointerDown', handleDragStart);
        };
    }, [emblaApi, getEmblaApi, handleSelect, markUserInitiated]);

    const contextValues: CarouselContextValues = useMemo(
        () => ({
            orientation,
            overflow,
            emblaRefElement,
            emblaApi,
            slideSpacing,
            visibleSlides,
            handleKeydown,
            title,
            userInitiatedRef,
            markUserInitiated,
        }),
        [
            orientation,
            overflow,
            emblaRefElement,
            emblaApi,
            slideSpacing,
            visibleSlides,
            handleKeydown,
            title,
            markUserInitiated,
        ]
    );

    return <CarouselProvider value={contextValues}>{children}</CarouselProvider>;
}
